import React, { useState, useEffect } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './screens/Login';
import PlansPage from './screens/Plans/index';
import ExperimentPage from './screens/Experiment/index';
import PromoCodesPage from './screens/PromoCodes/index';
import OrdersPage from './screens/Orders/index';
import PaymentsPage from './screens/Payments/index';
import SubscriptionsPage from './screens/Subscriptions/index';
import VideoLessonsPage from './screens/VideoLessons/index';
import CashflowPage from './screens/Cashflow/index';
import ExpensesPage from './screens/Expenses/index';
import FoundersPage from './screens/Founders/index';
import NotificationsPage from './screens/Notifications/index';
import CreateNotificationPage from './screens/CreateNotification/index';
import NotificationAnalyticsPage from './screens/NotificationAnalytics/index';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import KPICards from './components/Dashboard/KPICards';
import Charts from './components/Dashboard/Charts';
import ActivitySection from './components/Dashboard/ActivitySection';
import { cn } from './lib/utils';
import { Toaster } from './components/ui/sonner';
import { ConfirmProvider } from './components/shared/ConfirmDialog';
import UsersPage from './screens/Users/index';
import SettingsPage from './screens/Settings/index';
import SecurityPage from './screens/Security/index';
import GamesPage from './screens/Games/index';
import ExamAttemptsPage from './screens/ExamAttempts/index';
import WrongAnswersPage from './screens/WrongAnswers/index';
import AuditLogPage from './screens/AuditLog/index';
import AppConfigPage from './screens/AppConfig/index';
import BannersPage from './screens/Banners/index';
import DuplicatesPage from './screens/Duplicates/index';

interface PlaceholderPageProps {
  title: string;
  isDark: boolean;
}

interface MainLayoutProps {
  children: React.ReactNode;
  isDark: boolean;
  setIsDark: (value: boolean) => void;
}

interface DashboardPageProps {
  isDark: boolean;
}

// Dashboard Page
const DashboardPage: React.FC<DashboardPageProps> = ({ isDark }) => {
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className={cn(
          'text-2xl lg:text-3xl font-bold tracking-tight',
          isDark ? 'text-white' : 'text-[#111111]'
        )}>Главная</h1>
        <p className={cn(
          'text-sm mt-1',
          isDark ? 'text-slate-400' : 'text-slate-500'
        )}>С возвращением! Вот что происходит на платформе.</p>
      </div>

      {/* KPI Cards */}
      <KPICards isDark={isDark} />

      {/* Charts Section */}
      <Charts isDark={isDark} />

      {/* Activity Section */}
      {/* <ActivitySection isDark={isDark} /> */}
    </div>
  );
};

// Placeholder pages
const PlaceholderPage: React.FC<PlaceholderPageProps> = ({ title, isDark }) => (
  <div className="flex items-center justify-center h-[60vh]">
    <div className="text-center">
      <h1 className={cn(
        'text-2xl lg:text-3xl font-bold tracking-tight',
        isDark ? 'text-white' : 'text-[#111111]'
      )}>{title}</h1>
      <p className={cn(
        'text-sm mt-2',
        isDark ? 'text-slate-400' : 'text-slate-500'
      )}>Страница в разработке</p>
    </div>
  </div>
);

// Main Layout
const MainLayout: React.FC<MainLayoutProps> = ({ children, isDark, setIsDark }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className={cn(
      'min-h-screen transition-colors duration-300',
      isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]'
    )}>
      {/* Sidebar */}
      <div className="hidden lg:block">
        <Sidebar 
          collapsed={sidebarCollapsed} 
          setCollapsed={setSidebarCollapsed}
          isDark={isDark}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={cn(
        'lg:hidden fixed left-0 top-0 h-screen z-40 transition-transform duration-300',
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <Sidebar 
          collapsed={false} 
          setCollapsed={() => {}}
          isDark={isDark}
        />
      </div>

      {/* Header */}
      <Header 
        isDark={isDark}
        setIsDark={setIsDark}
        sidebarCollapsed={sidebarCollapsed}
        onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
      />

      {/* Main Content */}
      <main
        className={cn(
          'pt-16 transition-all duration-300 min-h-screen',
          // На мобильном контент на всю ширину; на десктопе отступ под сайдбар.
          'ml-0',
          sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        )}
      >
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

function App() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('adminDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Update body class and localStorage for dark mode
  useEffect(() => {
    localStorage.setItem('adminDarkMode', JSON.stringify(isDark));
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.body.style.backgroundColor = '#0F0F17';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.style.backgroundColor = '#F5F3EF';
    }
  }, [isDark]);

  return (
    <div className={cn('App', isDark && 'dark-mode')}>
      <ConfirmProvider isDark={isDark}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout isDark={isDark} setIsDark={setIsDark}>
                    <Routes>
                      <Route path="/" element={<DashboardPage isDark={isDark} />} />
                      <Route path="/users" element={<UsersPage isDark={isDark} />} />
                      <Route path="/duplicates" element={<DuplicatesPage isDark={isDark} />} />
                      <Route path="/security" element={<SecurityPage isDark={isDark} />} />
                      <Route path="/plans" element={<PlansPage isDark={isDark} />} />
                      <Route path="/experiment" element={<ExperimentPage isDark={isDark} />} />
                      <Route path="/video-lessons" element={<VideoLessonsPage isDark={isDark} />} />
                      <Route path="/promo-codes" element={<PromoCodesPage isDark={isDark} />} />
                      <Route path="/orders" element={<OrdersPage isDark={isDark} />} />
                      <Route path="/payments" element={<PaymentsPage isDark={isDark} />} />
                      <Route path="/subscriptions" element={<SubscriptionsPage isDark={isDark} />} />
                      <Route path="/notifications" element={<NotificationsPage isDark={isDark} />} />
                      <Route path="/notifications/create" element={<CreateNotificationPage isDark={isDark} />} />
                      <Route path="/notifications/:id/analytics" element={<NotificationAnalyticsPage isDark={isDark} />} />
                      <Route path="/games" element={<GamesPage isDark={isDark} />} />
                      <Route path="/exam-attempts" element={<ExamAttemptsPage isDark={isDark} />} />
                      <Route path="/wrong-answers" element={<WrongAnswersPage isDark={isDark} />} />
                      <Route path="/audit-log" element={<AuditLogPage isDark={isDark} />} />
                      <Route path="/cashflow" element={<CashflowPage isDark={isDark} />} />
                      <Route path="/expenses" element={<ExpensesPage isDark={isDark} />} />
                      <Route path="/founders" element={<FoundersPage isDark={isDark} />} />
                      <Route path="/banners" element={<BannersPage isDark={isDark} />} />
                      <Route path="/app-config" element={<AppConfigPage isDark={isDark} />} />
                      <Route path="/settings" element={<SettingsPage isDark={isDark} />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
      </ConfirmProvider>
    </div>
  );
}

export default App;

